/**
 * What the walk does to the page drawn in its frame, whichever layout draws it: holding Before shows a part as it was
 * until it is let go.
 */
import { type RefObject, useEffect } from "react";
import { type PageStudio, WHOLE_PAGE } from "./pageStudio";

/** The style the page wears while it is being walked through: parts outlined, the one being changed breathing. */
export const WALK_STYLE = `
[data-section]{position:relative;cursor:pointer;outline:2px solid transparent;outline-offset:6px;border-radius:6px;transition:outline-color .15s,filter .3s,opacity .3s}
[data-section]:hover{outline-color:rgba(122,35,56,.32)}
[data-section].bw-sel{outline-color:#7a2338;box-shadow:0 0 0 6px rgba(122,35,56,.08)}
[data-section][data-bwn]:not(.bw-sel)::after{content:attr(data-bwn);position:absolute;right:-4px;top:-12px;min-width:20px;height:20px;padding:0 6px;border-radius:100px;background:#7a2338;color:#fff;font:700 12px/20px 'Plus Jakarta Sans',system-ui,sans-serif;text-align:center;z-index:5}
body.bw-busy [data-section]:not(.bw-sel){filter:blur(1.5px) saturate(.6);opacity:.55}
[data-section].bw-sel.bw-work{animation:bwbreathe 1.2s ease-in-out infinite}
@keyframes bwbreathe{50%{box-shadow:0 0 0 12px rgba(122,35,56,.12)}}
[data-section].bw-landed{animation:bwlanded 1.6s ease-out}
[data-section].bw-pv{outline-color:#b0873a!important;animation:bwglow 1.6s ease-in-out infinite}
[data-section].bw-pv::after{content:"Before";position:absolute;right:12px;top:8px;z-index:4;height:22px;padding:0 9px;border-radius:6px;background:#b0873a;color:#fff;font:700 12px/22px 'Plus Jakarta Sans',system-ui,sans-serif;letter-spacing:0;text-transform:none;pointer-events:none}
@keyframes bwglow{0%,100%{outline-color:#b0873a}50%{outline-color:rgba(176,135,58,.4)}}
[data-slot][contenteditable]{cursor:text;border-radius:4px;transition:background .12s,box-shadow .12s}
[data-slot][contenteditable]:hover{background:rgba(176,135,58,.1)}
[data-slot][contenteditable]:focus{outline:none;background:rgba(176,135,58,.13);box-shadow:0 0 0 3px rgba(176,135,58,.13)}
@keyframes bwlanded{0%{background:rgba(47,122,90,.26);box-shadow:0 0 0 6px rgba(47,122,90,.16)}100%{background:transparent;box-shadow:none}}
`;

/**
 * Sizes the preview frame to the page's real height, with no scrollbar of its own, and again whenever the page grows
 * (its fonts arriving, a picture opening). Never shorter than the first screen.
 */
export function sizeFrame(frame: HTMLIFrameElement, fold: number, onHeight: (height: number) => void) {
  const d = frame.contentDocument;
  const root = d?.documentElement;
  if (!d || !root) return;
  root.style.overflow = "hidden";
  let last = 0;
  const measure = () => {
    // Why the frame is made the first screen's height before it is read: a frame taller than the page would never report a shorter one.
    frame.style.height = `${fold}px`;
    const h = Math.max(fold, root.scrollHeight);
    frame.style.height = `${h}px`;
    if (h !== last) onHeight(h);
    last = h;
  };
  measure();
  if (root.dataset.bwSized) return;
  root.dataset.bwSized = "1";
  void d.fonts?.ready.then(measure);
  if (typeof ResizeObserver !== "undefined") new ResizeObserver(measure).observe(root);
}

/** A slot's place among the slots of its name in the part, which is how the server finds it. */
export function slotIndex(part: Element, el: HTMLElement): number {
  const same = [...part.querySelectorAll<HTMLElement>("[data-slot]")].filter((s) => s.dataset.slot === el.dataset.slot);
  return Math.max(0, same.indexOf(el));
}

export function usePartBefore(frame: RefObject<HTMLIFrameElement | null>, open: string | null, studio: PageStudio, peek: boolean, dress: () => void) {
  // Holding Before shows the part as it was, in place, until it is let go.
  useEffect(() => {
    const d = frame.current?.contentDocument;
    if (!d || !open || open === WHOLE_PAGE) return;
    const el = d.querySelector<HTMLElement>(`[data-section="${CSS.escape(open)}"]`);
    const was = studio.before(open);
    if (!el || was === null) return;
    if (!peek) return;
    const now = el.outerHTML;
    el.outerHTML = was;
    d.querySelector<HTMLElement>(`[data-section="${CSS.escape(open)}"]`)?.classList.add("bw-sel", "bw-pv");
    return () => {
      const back = d.querySelector<HTMLElement>(`[data-section="${CSS.escape(open)}"]`);
      if (back) back.outerHTML = now;
      // Why: the part put back is a new element with the old mark on it, and it needs its listeners again.
      d.querySelector<HTMLElement>(`[data-section="${CSS.escape(open)}"]`)?.removeAttribute("data-bw-wired");
      dress();
    };
  }, [frame, peek, open, studio, dress]);
}
